import {ViewedFileProgress} from './ViewedFileProgress'
import {Stack} from '@primer/react'
import {useState} from 'react'
import stickyStyles from '@github-ui/use-sticky-header/use-sticky-header.module.css'
import {useIntersectionObserver} from '@github-ui/use-sticky-header/useIntersectionObserver'
import {clsx} from 'clsx'
import styles from './PullRequestFilesToolbar.module.css'
import {useIsSingleFileMode} from '../../stores/selectors'
import {ReviewNavigationButtonGroup} from './ReviewNavigationButtonGroup'
import {DiffViewSettings, type AvailableDiffViewSettings} from '@github-ui/diff-view-settings'
import type {PullRequestNotices, UserNotice} from '../../page-data/payloads/notices'

export type BasicDiffComparisonViewToolbarProps = {
  fileFilter: React.ReactElement
  isFileTreeExpanded?: boolean
  shouldShowViewedFilesCount: boolean
  totalFilesCount: number
  treeToggleElement?: React.ReactElement
  userNotices?: Array<UserNotice<PullRequestNotices>>
}

export function BasicDiffComparisonViewToolbar({
  fileFilter,
  isFileTreeExpanded,
  shouldShowViewedFilesCount,
  totalFilesCount,
  treeToggleElement,
  userNotices,
}: BasicDiffComparisonViewToolbarProps) {
  const enabledDiffViewSettings: AvailableDiffViewSettings[] = ['line-spacing'] as const
  const [isSticky, setIsSticky] = useState(false)

  const isSingleFileMode = useIsSingleFileMode()
  const showDivider = shouldShowViewedFilesCount || !isFileTreeExpanded

  const sensorRef = useIntersectionObserver(entries => {
    if (entries[0]) {
      setIsSticky(!entries[0].isIntersecting)
    }
  })

  return (
    <>
      <div ref={sensorRef} className={styles.stickyHeaderActivationThreshold} />
      <Stack
        as="section"
        direction="horizontal"
        justify="space-between"
        gap="condensed"
        align="center"
        className={clsx(stickyStyles.stickyHeader, styles['simple-toolbar'], isSticky && styles['is-stuck'])}
      >
        <h2 className="sr-only">Diff Comparison Toolbar</h2>

        <Stack direction="horizontal" gap="condensed" align="center" className="min-width-0">
          <div>{treeToggleElement}</div>
        </Stack>
        <Stack direction="horizontal" align="center">
          <div className={styles['file-controls']}>
            {shouldShowViewedFilesCount && <ViewedFileProgress totalFilesCount={totalFilesCount} />}

            {!isFileTreeExpanded && fileFilter}
            {isSingleFileMode && totalFilesCount > 1 && (
              <div className={styles['file-controls-navigation']}>
                <ReviewNavigationButtonGroup userNotices={userNotices} />
              </div>
            )}
          </div>

          {showDivider && <div className={styles['file-controls-divider']} data-testid="file-controls-divider" />}

          <DiffViewSettings
            invisible={false}
            small
            enabledDiffViewSettings={enabledDiffViewSettings}
            userNotices={userNotices}
          />
        </Stack>
      </Stack>
    </>
  )
}
